import { useState, useEffect } from 'react';
import { useLoyalty } from '../../../contexts/LoyaltyContext';
import { apiGet } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';

/** Shape returned by GET /customer/loyalty/badges?programId=… */
interface ApiBadgeProgress {
  badge: {
    id: string;
    code: string;
    name: string;
    description: string | null;
    iconUrl: string | null;
    conditionType: string;
    conditionThreshold: number;
    benefitType: string;
    benefitValue: string | null;
    benefitDurationDays: number | null;
    displayOrder: number;
  };
  current: number;
  required: number;
  progressPercent: number;
  unlocked: boolean;
}

/** Normalised badge for display */
interface Badge {
  id: string;
  name: string;
  description: string | null;
  iconUrl: string | null;
  rarity: string;
  benefitType: string;
  unlocked: boolean;
  progress: { current: number; target: number; percent: number };
}

interface LoyaltyBadgesGridProps {
  showFilters?: boolean;
  showStats?: boolean;
  columns?: '2' | '3' | '4';
}

type FilterTab = 'all' | 'earned' | 'locked';

function LoginCta() {
  return (
    <div className="loyalty-card p-8 text-center">
      <div className="text-4xl mb-3 opacity-50">&#127942;</div>
      <h2 className="loyalty-heading text-xl mb-2 text-[var(--color-text)]">
        Login to view your badges
      </h2>
      <p className="text-[var(--color-text-muted)] text-sm mb-4">
        Sign in to see your achievements and progress.
      </p>
      <a
        href="/login"
        className="inline-block px-6 py-2 rounded-lg bg-[var(--color-primary)] text-white font-semibold text-sm hover:opacity-90 transition-opacity"
      >
        Sign In
      </a>
    </div>
  );
}

function deriveRarity(threshold: number): string {
  if (threshold <= 1) return 'common';
  if (threshold <= 5) return 'uncommon';
  if (threshold <= 10) return 'rare';
  if (threshold <= 25) return 'epic';
  return 'legendary';
}

const RARITY_COLORS: Record<string, string> = {
  common: '#9ca3af',
  uncommon: '#22c55e',
  rare: '#3b82f6',
  epic: '#a855f7',
  legendary: '#f59e0b',
};

function normalise(raw: ApiBadgeProgress[]): Badge[] {
  return raw.map((r) => ({
    id: r.badge.id,
    name: r.badge.name,
    description: r.badge.description,
    iconUrl: r.badge.iconUrl,
    rarity: deriveRarity(r.badge.conditionThreshold),
    benefitType: r.badge.benefitType,
    unlocked: r.unlocked,
    progress: {
      current: r.current,
      target: r.required,
      percent: r.progressPercent,
    },
  }));
}

function BadgeCard({ badge }: { badge: Badge }) {
  const progressPct = badge.progress.target > 0
    ? Math.min(100, Math.round((badge.progress.current / badge.progress.target) * 100))
    : 0;

  return (
    <div
      className={`loyalty-card p-4 flex flex-col items-center text-center transition-all ${
        badge.unlocked ? '' : 'opacity-50 grayscale'
      }`}
      style={{
        borderColor: badge.unlocked ? 'var(--color-primary)' : undefined,
        borderWidth: badge.unlocked ? '2px' : undefined,
      }}
    >
      {/* Icon */}
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center text-2xl mb-3"
        style={{
          backgroundColor: badge.unlocked
            ? (RARITY_COLORS[badge.rarity] ?? 'var(--color-primary)') + '20'
            : 'var(--color-surface-border)',
        }}
      >
        {badge.iconUrl ? (
          <img src={badge.iconUrl} alt={badge.name} className="w-10 h-10 object-contain" />
        ) : (
          <span>&#127942;</span>
        )}
      </div>

      {/* Rarity tag */}
      <span
        className="loyalty-tag-skew text-[10px] font-bold uppercase px-2 py-0.5 rounded-full text-white mb-2"
        style={{ backgroundColor: RARITY_COLORS[badge.rarity] ?? '#6b7280' }}
      >
        {badge.rarity}
      </span>

      {/* Name */}
      <h3 className="loyalty-heading text-sm text-[var(--color-text)] mb-1">{badge.name}</h3>

      {/* Description */}
      {badge.description && (
        <p className="text-[var(--color-text-muted)] text-xs mb-3 line-clamp-2">{badge.description}</p>
      )}

      {/* Status */}
      {badge.unlocked ? (
        <span className="text-xs text-[var(--color-primary)] font-semibold">
          &#10003; Earned
        </span>
      ) : (
        <div className="w-full">
          <div className="loyalty-progress-bar h-1.5 rounded-full overflow-hidden bg-[var(--color-surface-border)] mb-1">
            <div
              className="h-full rounded-full bg-[var(--color-primary)] transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span className="text-[10px] text-[var(--color-text-muted)]">
            {badge.progress.current} / {badge.progress.target}
          </span>
        </div>
      )}
    </div>
  );
}

export function LoyaltyBadgesGridBlock({ props }: { props: LoyaltyBadgesGridProps }) {
  const { isAuthenticated } = useAuth();
  const { data, loading: loyaltyLoading } = useLoyalty();
  const showFilters = props.showFilters ?? true;
  const showStats = props.showStats ?? true;
  const columns = props.columns ?? '3';

  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');

  useEffect(() => {
    if (!isAuthenticated || !data?.programId) return;
    let cancelled = false;
    setLoading(true);
    apiGet<ApiBadgeProgress[]>(`/customer/loyalty/badges?programId=${data.programId}`)
      .then((res) => {
        if (!cancelled) setBadges(normalise(Array.isArray(res) ? res : []));
      })
      .catch(() => { if (!cancelled) setBadges([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated, data?.programId]);

  if (loyaltyLoading || loading) {
    return (
      <div className="loyalty-card p-8 animate-pulse">
        <div className="h-6 w-48 bg-[var(--color-surface-border)] rounded mb-4" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-40 bg-[var(--color-surface-border)] rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) return <LoginCta />;

  const filtered = badges.filter((b) => {
    if (activeFilter === 'earned') return b.unlocked;
    if (activeFilter === 'locked') return !b.unlocked;
    return true;
  });

  const gridCols = {
    '2': 'grid-cols-2',
    '3': 'grid-cols-2 md:grid-cols-3',
    '4': 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
  }[columns];

  const stats = data.stats;
  const earnedCount = badges.filter((b) => b.unlocked).length;
  const lockedCount = badges.filter((b) => !b.unlocked).length;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="loyalty-heading text-xl text-[var(--color-text)]">Achievement Wall</h2>
        {showFilters && (
          <div className="flex gap-1 bg-[var(--color-surface)] rounded-lg p-1 border border-[var(--color-surface-border)]">
            {(['all', 'earned', 'locked'] as FilterTab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveFilter(tab)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors capitalize ${
                  activeFilter === tab
                    ? 'bg-[var(--color-primary)] text-white'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                }`}
              >
                {tab} ({tab === 'earned' ? earnedCount : tab === 'locked' ? lockedCount : badges.length})
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Badge grid */}
      {filtered.length > 0 ? (
        <div className={`grid ${gridCols} gap-4`}>
          {filtered.map((badge) => (
            <BadgeCard key={badge.id} badge={badge} />
          ))}
        </div>
      ) : (
        <div className="loyalty-card p-8 text-center text-[var(--color-text-muted)] text-sm">
          No badges found in this category.
        </div>
      )}

      {/* Stats footer */}
      {showStats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {[
            { label: 'Active Streaks', value: stats.currentStreak, icon: '\uD83D\uDD25' },
            { label: 'Global Rank', value: stats.globalRank ? `#${stats.globalRank}` : '-', icon: '\uD83C\uDF0D' },
            { label: 'Challenges Won', value: stats.challengesWon, icon: '\uD83C\uDFC6' },
            { label: 'Badges Earned', value: `${stats.unlockedBadges}/${stats.totalBadges}`, icon: '\uD83C\uDFC5' },
          ].map((stat) => (
            <div key={stat.label} className="loyalty-card p-3 text-center">
              <div className="text-lg mb-1">{stat.icon}</div>
              <div className="text-lg font-bold text-[var(--color-primary)]">{stat.value}</div>
              <div className="text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider">{stat.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
